/**
 * Public page for accepting an invite link.
 *
 * Route: /accept-invite?token=xxx
 *
 * On mount: validates the token via GET /auth/invite/{token}.
 * If valid: shows a "Set up your account" form.
 * On submit: calls POST /auth/invite/{token}/accept, stores tokens, redirects to /.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateInviteToken, acceptInvite } from '@/api/users';
import { useAuthStore } from '@/store/authStore';
import type { InviteTokenInfo } from '@/types/invitation';
import {startSso,ssoProviderNames} from '@/api/identity';
import {coreApiMode} from '@/config/deployment';
import BrandTitle from '@/components/common/BrandTitle';
import BrandMark from '@/components/common/BrandMark';

type PageState = 'loading' | 'valid' | 'invalid' | 'submitting' | 'error';

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [tokenInfo, setTokenInfo] = useState<InviteTokenInfo | null>(null);
  const ssoName = ssoProviderNames[tokenInfo?.sso_provider ?? 'microsoft'];
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    validateInviteToken(token)
      .then((info) => {
        setTokenInfo(info);
        if (info.full_name) setFullName(info.full_name);
        setPageState('valid');
      })
      .catch(() => {
        setPageState('invalid');
      });
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!fullName.trim()) {
      setFormError('Full name is required.');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setPageState('submitting');

    try {
      const tokens = await acceptInvite(token, { full_name: fullName.trim(), password });

      // Store tokens and load user profile
      const kind=await useAuthStore.getState().acceptAuthentication(tokens);
      navigate(kind==='enrollment'?'/enroll':kind==='mfa'?'/login':'/', { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? 'Failed to accept invitation. Please try again.';
      setFormError(detail);
      setPageState('valid');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="mb-8 text-center">
          <BrandMark className="mx-auto h-14 w-14" />
          <BrandTitle/>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {pageState === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4" />
              <p className="text-sm text-gray-500">Validating invitation…</p>
            </div>
          )}

          {pageState === 'invalid' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🔗</div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Invite link invalid</h2>
              <p className="text-sm text-gray-500">
                This invite link has expired, already been used, or is invalid.
                Please contact your administrator to send a new invitation.
              </p>
            </div>
          )}

          {coreApiMode&&tokenInfo?.sso_enabled&&<button className="btn-primary mb-4 w-full" onClick={async()=>{try{await startSso({mode:'invite',invite_token:token});}catch(e:any){setFormError(e?.response?.data?.detail??`${ssoName} sign-in unavailable`);}}}>Accept invitation with {ssoName}</button>}
          {tokenInfo?.sso_required&&<p className="text-sm">This organization requires {ssoName} sign-in. Use the invitation button above.</p>}
          {(pageState === 'valid' || pageState === 'submitting' || pageState === 'error') && tokenInfo && !tokenInfo.sso_required && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Set up your account</h2>
                <p className="text-sm text-gray-500">
                  You've been invited to join{' '}
                  <span className="font-medium text-gray-700">{tokenInfo.tenant_name}</span>{' '}
                  as a{' '}
                  <span className="font-medium text-gray-700">
                    {tokenInfo.role.replace('_', ' ')}
                  </span>
                  .
                </p>
                <p className="mt-1 text-sm text-gray-400">{tokenInfo.email}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {formError && (
                  <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    className="input-field mt-1"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your full name"
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    className="input-field mt-1"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <input
                    className="input-field mt-1"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={pageState === 'submitting'}
                  className="btn-primary w-full mt-2"
                >
                  {pageState === 'submitting' ? 'Creating account…' : 'Create account & sign in'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
